import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useAdjustWallet, useOrganizationWallet } from "@/hooks/useSubscription";
import { Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

const formSchema = z.object({
  amount: z.number().min(1, "Amount must be at least 1 cent")
});

interface AdjustWalletDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizationId: string;
}

export const AdjustWalletDialog = ({
  open,
  onOpenChange,
  organizationId
}: AdjustWalletDialogProps) => {
  const { toast } = useToast();
  const { data: wallet, isLoading: isLoadingWallet } = useOrganizationWallet(organizationId);
  const { mutate: adjustWallet, isPending } = useAdjustWallet();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      amount: 0
    }
  });

  const onSubmit = (values: z.infer<typeof formSchema>) => {
    adjustWallet(
      { organizationId, amount: values.amount },
      {
        onSuccess: () => {
          toast({
            title: "Wallet adjusted",
            description: "The wallet balance has been successfully adjusted."
          });
          onOpenChange(false);
          form.reset();
        },
        onError: () => {
          toast({
            variant: "destructive",
            title: "Error",
            description: "Failed to adjust wallet balance. Please try again."
          });
        }
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Adjust Wallet Balance</DialogTitle>
          <DialogDescription>
            Add funds to the organization wallet. Amount should be in cents.
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          {isLoadingWallet ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          ) : (
            <div className="mb-4 rounded-lg bg-muted p-4">
              <div className="text-sm font-medium text-muted-foreground">Current Balance</div>
              <div className="text-2xl font-bold">
                {wallet ? (wallet.balance / 100).toFixed(2) : "0.00"} SAR
              </div>
            </div>
          )}

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="amount"
                render={({ field: { onChange, ...field } }) => (
                  <FormItem>
                    <FormLabel>Amount (Cents)</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        placeholder="1000"
                        {...field}
                        onChange={(e) => onChange(Number(e.target.value))}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <DialogFooter>
                <Button type="submit" disabled={isPending}>
                  {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Adjust Balance
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </div>
      </DialogContent>
    </Dialog>
  );
};
